import { ExportSquare, Like1, Refresh2 } from "iconsax-reactjs";
import { X } from "lucide-react";
import {
  BodyText,
  Flyin,
  FlyinBody,
  FlyinHeader,
  FlyinTitle,
} from "../../../shared/ui";
import { KybRequirement } from "../types";
import { approvedActionColor } from "./kybShowReviewShared";

type KybComplianceDocumentIssueFlyinProps = {
  open: boolean;
  document: KybRequirement | null;
  onOpenChange: (open: boolean) => void;
  onApprove: () => void;
  onReUpload: () => void;
  onViewFull?: () => void;
};

const formatFlaggedDate = (value?: string) => {
  if (!value) return "[-]";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "[-]";

  return `${new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date)}`;
};

const KybComplianceDocumentIssueFlyin = ({
  open,
  document,
  onOpenChange,
  onApprove,
  onReUpload,
  onViewFull,
}: KybComplianceDocumentIssueFlyinProps) => {
  return (
    <Flyin
      open={open}
      onOpenChange={onOpenChange}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[520px] rounded-l-[16px]"
    >
      <FlyinHeader className="items-center border-b border-[#E6E9F5] pb-4">
        <FlyinTitle className="aku-heading-h3-semibold">
          {document?.key || "Document"}
        </FlyinTitle>
        <button
          type="button"
          aria-label="Close document issue details"
          onClick={() => onOpenChange(false)}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF]"
        >
          <X size={20} />
        </button>
      </FlyinHeader>

      <FlyinBody className="px-6 py-0">
        <div className="flex items-center gap-8 border-b border-[#E6E9F5] py-4">
          <button
            type="button"
            onClick={onApprove}
            className="inline-flex items-center gap-3 aku-body-small-medium text-[#161827] hover:opacity-80"
          >
            <Like1 size={20} color={approvedActionColor} />
            Approve
          </button>
          <button
            type="button"
            onClick={onReUpload}
            className="inline-flex items-center gap-3 aku-body-small-medium text-[#161827] hover:opacity-80"
          >
            <Refresh2 size={20} />
            Re-upload
          </button>
          {onViewFull ? (
            <button
              type="button"
              onClick={onViewFull}
              className="inline-flex items-center gap-3 aku-body-small-medium text-[#161827] hover:opacity-80"
            >
              <ExportSquare size={20} />
              View full
            </button>
          ) : null}
        </div>

        <section className="mt-8">
          <BodyText className="aku-body-small-medium text-[#161827]">
            Issue
          </BodyText>
          <div className="mt-3 rounded-[12px] bg-[#F7F8FD] p-4">
            <div className="grid grid-cols-[140px_1fr] gap-y-5">
              <BodyText className="aku-body-small-regular text-[#6F739B]">
                Reason
              </BodyText>
              <BodyText className="text-right aku-body-small-medium text-[#5E01D6]">
                {document?.comment
                  ? `${document.comment}`
                  : "[Document is invalid]"}
              </BodyText>

              <BodyText className="aku-body-small-regular text-[#6F739B]">
                Description
              </BodyText>
              <BodyText className="text-right aku-body-small-regular text-[#383B56]">
                This document was flagged during review and requires an updated
                upload.
              </BodyText>

              <BodyText className="aku-body-small-regular text-[#6F739B]">
                Flagged date
              </BodyText>
              <BodyText className="text-right aku-body-small-regular text-[#161827]">
                {formatFlaggedDate(document?.reviewedAt)}
              </BodyText>
            </div>
          </div>
        </section>

        <section className="mt-8">
          <BodyText className="aku-body-small-medium text-[#161827]">
            Timeline
          </BodyText>
          <div className="relative mt-8 space-y-8 before:absolute before:left-[5px] before:top-3 before:h-[calc(100%-20px)] before:w-px before:bg-[#E1E6FF]">
            <div className="relative pl-8">
              <span className="absolute left-0 top-8 z-[1] h-3 w-3 rounded-full bg-[#1DCB1C]" />
              <BodyText className="aku-body-small-regular text-[#6F739B]">
                Aug 5th, 2025 • 5:30 AM
              </BodyText>
              <BodyText className="mt-2 aku-body-small-medium text-[#161827]">
                Document reuploaded
              </BodyText>
              <BodyText className="mt-2 aku-body-small-regular text-[#383B56]">
                Philip
              </BodyText>
            </div>
            <div className="relative pl-8">
              <span className="absolute left-0 top-8 z-[1] h-3 w-3 rounded-full bg-[#DDE2FA]" />
              <BodyText className="aku-body-small-regular text-[#6F739B]">
                Aug 5th, 2025 • 5:30 AM
              </BodyText>
              <BodyText className="mt-2 aku-body-small-regular text-[#383B56]">
                Document flagged
              </BodyText>
              <BodyText className="mt-2 aku-body-small-regular text-[#383B56]">
                Sadiq
              </BodyText>
            </div>
          </div>
        </section>
      </FlyinBody>
    </Flyin>
  );
};

export default KybComplianceDocumentIssueFlyin;
